import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2, TrendingUp, Shield, Clock, Users, Zap, BarChart3, Phone, Mail, MapPin, Sliders, Lock } from 'lucide-react';

const Homepage = () => {
  const features = [
    {
      icon: <TrendingUp className="w-8 h-8 text-primary-600" />,
      title: "You're in Control",
      description: "Choose the right finance option for your business based on personalised quotes from multiple lenders.",
    },
    {
      icon: <Zap className="w-8 h-8 text-primary-600" />,
      title: "Simple & Fast",
      description: "Submit a single funding request and receive offers from multiple lenders quickly.",
    },
    {
      icon: <BarChart3 className="w-8 h-8 text-primary-600" />,
      title: "Compare Offers Easily",
      description: "Review funding options side by side and choose the best terms for your business.",
    },
    {
      icon: <Shield className="w-8 h-8 text-primary-600" />,
      title: "No Credit Score Impact",
      description: "Initial funding checks will not affect your credit score.",
    },
  ];

  const steps = [
    {
      step: "01",
      icon: <CheckCircle2 className="w-12 h-12 text-accent-500" />,
      title: "Submit Your Funding Request",
      description: "Tell us about your business and funding needs using a simple online form.",
    },
    {
      step: "02",
      icon: <Users className="w-12 h-12 text-accent-500" />,
      title: "Compare Lender Offers",
      description: "Receive personalised offers from multiple lenders and compare terms.",
    },
    {
      step: "03",
      icon: <TrendingUp className="w-12 h-12 text-accent-500" />,
      title: "Get Funded",
      description: "Choose the best option and receive funding for your business.",
    },
  ];

  const trustFeatures = [
    {
      icon: <Sliders className="w-12 h-12 text-primary-600" />,
      title: "You're in Control",
      description: "Choose the right finance for your business based on personalised quotes from more than 70 lenders.",
    },
    {
      icon: <Zap className="w-12 h-12 text-primary-600" />,
      title: "Simple & Fast",
      description: "Complete one funding request in 3 minutes to access quotes, receive money in your account in 24 hours.",
    },
    {
      icon: <TrendingUp className="w-12 h-12 text-primary-600" />,
      title: "Fair & Transparent",
      description: "Easily compare the terms available to your business – no jargon, and never more expensive than direct.",
    },
    {
      icon: <Lock className="w-12 h-12 text-primary-600" />,
      title: "Safe & Secure",
      description: "Apply with confidence, no impact on your credit score, we're regulated by the FCA, accredited by HM Treasury.",
    },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="section bg-gradient-to-br from-primary-50 via-white to-accent-50">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-primary-900 leading-tight">
                Putting You in{' '}
                <span className="gradient-text">Control</span> of Your{' '}
                <span className="gradient-text">Business Funding</span>
              </h1>
              <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
                Access funding from 70+ trusted lenders with one simple request.
                Compare offers easily and choose the best financing option for your business.
                Apply with confidence — with no impact on your credit score.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/apply-for-funding" className="btn-primary text-center">
                  Apply for Funding
                  <ArrowRight className="inline ml-2 w-5 h-5" />
                </Link>
                <Link to="/knowledge/how-to-fund" className="btn-secondary text-center">
                  Learn More
                </Link>
              </div>
              <p className="text-sm text-gray-500 flex items-center">
                <Clock className="w-4 h-4 mr-2 text-accent-500" />
                You're only a few minutes away from personalised funding offers.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative z-10">
                <svg viewBox="0 0 500 500" className="w-full h-auto">
                  <defs>
                    <linearGradient id="heroGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#0056d6" />
                      <stop offset="100%" stopColor="#0ea5e9" />
                    </linearGradient>
                  </defs>
                  <circle cx="250" cy="250" r="200" fill="url(#heroGradient)" opacity="0.1" />
                  <circle cx="250" cy="250" r="150" fill="url(#heroGradient)" opacity="0.2" />
                  <g transform="translate(150, 100)">
                    <rect x="50" y="50" width="200" height="300" rx="10" fill="white" stroke="#0056d6" strokeWidth="2" />
                    <rect x="70" y="80" width="160" height="20" rx="5" fill="#e6f0ff" />
                    <rect x="70" y="120" width="100" height="15" rx="5" fill="#f0f9ff" />
                    <rect x="70" y="150" width="140" height="15" rx="5" fill="#f0f9ff" />
                    <rect x="70" y="180" width="120" height="15" rx="5" fill="#f0f9ff" />
                    <rect x="70" y="220" width="160" height="40" rx="5" fill="#0056d6" />
                    <circle cx="200" cy="280" r="30" fill="#0ea5e9" opacity="0.3" />
                    <circle cx="120" cy="280" r="20" fill="#0056d6" opacity="0.2" />
                  </g>
                </svg>
              </div>
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-accent-400 rounded-full filter blur-3xl opacity-20 -z-10 animate-pulse-slow" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trusted by Businesses and Financial Partners Section */}
      <section className="section bg-white">
        <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="max-w-4xl mx-auto text-center"
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-900 mb-6">
                Trusted by Leading Lenders & Partners
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed mb-8">
                1ST TRUST FINANCES works with more than 80 banks, lenders, and funders. Our technology and analytics solutions are transforming customer conversion and engagement, setting the standard in SME lending portfolio monitoring.
              </p>
            </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {trustFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gradient-to-br from-primary-50 to-white rounded-xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="mb-6">{feature.icon}</div>
                <h3 className="font-heading text-xl font-bold text-primary-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section bg-gradient-to-b from-primary-50 to-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-900 mb-4">
              Why Choose 1ST TRUST FINANCES?
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              We make business funding simple, fast, and transparent.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="mb-6">{feature.icon}</div>
                <h3 className="font-heading text-xl font-bold text-primary-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="section bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-900 mb-4">
              How It Works
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Get funded in three simple steps.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                className="relative"
              >
                <div className="bg-gradient-to-br from-primary-50 to-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
                  <div className="text-6xl font-bold text-primary-200 mb-4">
                    {step.step}
                  </div>
                  <div className="mb-6">{step.icon}</div>
                  <h3 className="font-heading text-xl font-bold text-primary-900 mb-3">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {step.description}
                  </p>
                </div>
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <ArrowRight className="w-8 h-8 text-primary-300" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Call To Action Section */}
      <section className="section gradient-bg text-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              Have 70+ Lenders Compete to Fund Your Business
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Receive funding offers within minutes.
            </p>
            <Link to="/apply-for-funding" className="inline-block bg-white text-primary-600 font-semibold px-8 py-4 rounded-lg hover:bg-gray-100 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1">
              Apply Now
              <ArrowRight className="inline ml-2 w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-900 mb-6">
                Get in Touch
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Our team at 1ST TRUST FINANCES is ready to help you find the right funding solution for your business.
              </p>
              <div className="space-y-6">
                {/* {/* <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-primary-600" />
                  </div>
                  {/* <div>
                    <h4 className="font-semibold text-primary-900 mb-1">Phone</h4>
                    <a href="tel:+441234567890" className="text-gray-600 hover:text-primary-600 transition-colors">
                      +44 (0) 123 456 7890
                    </a>
                  </div> *
                </div> */}
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-primary-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-primary-900 mb-1">Email</h4>
                    <a href="mailto:Support@1stTrustfi.com" className="text-gray-600 hover:text-primary-600 transition-colors">
                      Support@1stTrustfi.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-primary-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-primary-900 mb-1">Office Address</h4>
                    <p className="text-gray-600">
                      The Manor House 260 Ecclesall road south<br />
                      Sheffield, S11 9PS UNITED KINGDOM
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <svg viewBox="0 0 500 400" className="w-full h-auto">
                <defs>
                  <linearGradient id="contactGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#0056d6" />
                    <stop offset="100%" stopColor="#0ea5e9" />
                  </linearGradient>
                </defs>
                <circle cx="250" cy="200" r="150" fill="url(#contactGradient)" opacity="0.1" />
                <g transform="translate(100, 50)">
                  <rect x="50" y="50" width="300" height="200" rx="10" fill="white" stroke="#0056d6" strokeWidth="2" />
                  <rect x="70" y="80" width="260" height="30" rx="5" fill="#e6f0ff" />
                  <rect x="70" y="130" width="120" height="30" rx="5" fill="#e6f0ff" />
                  <rect x="200" y="130" width="130" height="30" rx="5" fill="#e6f0ff" />
                  <rect x="70" y="180" width="260" height="50" rx="5" fill="#0056d6" />
                </g>
              </svg>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Homepage;
